/**
 * ObituaryNoticePage — /obituary/:slug
 *
 * The DATED obituary notice: a compact, newspaper-style announcement
 * for the days-and-weeks following a passing. Distinct from the
 * PERMANENT memorial page (/memorial/:slug) which carries the life
 * story, media wall and tribute wall.
 *
 * Both pages share the same underlying `memorial` record (fetched from
 * /api/memorials/:slug) so the person identity stays single-sourced.
 * The obituary page:
 *   - noindexes automatically after 365 days
 *   - focuses on service info + family + brief remembrance
 *   - links to the permanent memorial for the full life story
 *
 * Rationale: search engines historically rank a dated obituary notice
 * separately from an evergreen memorial (news vs. reference intent).
 * Splitting the URL lets us claim both patterns without the current
 * duplicate-URL confusion where /celebrate/:slug served both roles.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { http } from "@/lib/api";
import { Flame, Calendar, MapPin, Users, ArrowRight, Loader2, ExternalLink } from "lucide-react";

const P = {
  ink: "#1F1A15",
  inkSoft: "#5E4E3A",
  paper: "#FFF9EF",
  rule: "#D9C79A",
  gold: "#B58936",
  saffron: "#B25D38",
  tulsi: "#0E7C55",
};

function fmtDate(d) {
  if (!d) return "";
  const s = String(d);
  // year-only stays as-is
  if (/^\d{4}$/.test(s)) return s;
  const dt = new Date(s);
  if (isNaN(dt)) return s;
  return dt.toLocaleDateString(undefined, { day: "numeric", month: "long", year: "numeric" });
}

function daysSince(d) {
  if (!d) return null;
  const dt = new Date(d);
  if (isNaN(dt)) return null;
  return Math.floor((Date.now() - dt.getTime()) / (1000 * 60 * 60 * 24));
}

export default function ObituaryNoticePage() {
  const { slug } = useParams();
  const [m, setM] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await http.get(`/memorials/${slug}`);
        if (alive) setM(data);
      } catch (e) {
        if (alive) setError(e?.response?.status === 404 ? "not_found" : "error");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [slug]);

  // 365-day expiry rule: after a year, the notice is noindexed and the
  // canonical URL points to the permanent memorial. Search engines
  // stop competing between the two URLs after the news-freshness window.
  const noticeAgeDays = useMemo(() => {
    if (!m) return null;
    return daysSince(m.death_date || m.published_at || m.created_at);
  }, [m]);
  const isFreshNotice = noticeAgeDays === null || noticeAgeDays <= 365;

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center" style={{ background: P.paper }}>
        <Loader2 className="w-6 h-6 animate-spin" style={{ color: P.saffron }} />
      </div>
    );
  }
  if (error === "not_found" || !m) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center px-4 text-center" style={{ background: P.paper, color: P.ink }}>
        <Helmet><title>Obituary notice not found</title><meta name="robots" content="noindex,follow" /></Helmet>
        <div className="text-5xl" style={{ color: P.gold }}>❦</div>
        <h1 className="mt-4 font-display text-3xl">Obituary notice not found</h1>
        <p className="mt-2 max-w-md text-sm" style={{ color: P.inkSoft }}>
          The obituary you're looking for may have been merged into a permanent memorial.{" "}
          <Link to="/obituaries" className="underline" style={{ color: P.saffron }}>Browse recent obituaries →</Link>
        </p>
      </div>
    );
  }

  const name = m.preferred_name || m.name_latin || m.full_name || "In loving memory";
  const nameMl = m.name_ml || m.name_local || null;
  const age = m.age_at_death;
  const born = m.birth_date;
  const died = m.death_date;
  const loc = m.location && typeof m.location === "object"
    ? [m.location.city, m.location.state, m.location.country].filter(Boolean).join(", ")
    : (m.location || "");
  const service = m.service_info || m.funeral_details || m.funeral || null;
  const family = Array.isArray(m.survivors) && m.survivors.length ? m.survivors : (m.family_members || []);
  const brief = m.short_remembrance || m.summary || m.obituary_summary || m.excerpt || null;
  const permanentUrl = `/memorial/${m.slug || m.id}`;
  const canonicalUrl = isFreshNotice ? `/obituary/${m.slug || m.id}` : permanentUrl;

  const title = `Obituary of ${name}${age ? `, ${age}` : ""}${loc ? ` — ${loc}` : ""} · IndianFamilyTree`;
  const description = brief
    ? String(brief).slice(0, 155)
    : `Obituary notice for ${name}${died ? `, who passed away ${fmtDate(died)}` : ""}${loc ? ` in ${loc}` : ""}. Service details, family and tributes.`;

  return (
    <div className="min-h-screen" style={{ background: P.paper, color: P.ink }}>
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={`https://www.indianfamilytree.com${canonicalUrl}`} />
        <meta name="robots" content={isFreshNotice ? "index,follow" : "noindex,follow"} />
        <meta property="og:type" content="article" />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        {m.portrait && <meta property="og:image" content={m.portrait} />}
        {/* NewsArticle schema — obituary notices behave like news for a
            year, then transition to the memorial as the canonical page. */}
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "NewsArticle",
          "headline": `Obituary of ${name}`,
          "datePublished": m.published_at || m.created_at,
          "dateModified": m.updated_at || m.created_at,
          "author": { "@type": "Organization", "name": "IndianFamilyTree" },
          "publisher": { "@type": "Organization", "name": "IndianFamilyTree",
                         "logo": { "@type": "ImageObject", "url": "https://www.indianfamilytree.com/favicon.png" } },
          "image": m.portrait || undefined,
          "mainEntityOfPage": `https://www.indianfamilytree.com${canonicalUrl}`,
          "about": {
            "@type": "Person",
            "name": name,
            "birthDate": born || undefined,
            "deathDate": died || undefined,
            "deathPlace": loc ? { "@type": "Place", "name": loc } : undefined,
          },
        })}</script>
      </Helmet>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10 sm:py-16">
        <div className="text-[10px] font-mono uppercase tracking-widest text-center" style={{ color: P.gold }} data-testid="obituary-kicker">
          Obituary notice · {fmtDate(died) || "date unknown"}
        </div>
        <div className="mt-4 flex flex-col items-center">
          {m.portrait ? (
            <img
              src={m.portrait}
              alt={name}
              className="w-28 h-28 sm:w-32 sm:h-32 rounded-full object-cover border-4"
              style={{ borderColor: P.gold }}
              loading="eager"
              data-testid="obituary-portrait"
            />
          ) : (
            <div className="w-28 h-28 rounded-full flex items-center justify-center border-4" style={{ borderColor: P.gold, background: "#F5EBD0", color: P.saffron }}>
              <Flame className="w-10 h-10" />
            </div>
          )}
          <h1 className="mt-5 font-display italic text-4xl sm:text-5xl text-center leading-tight" style={{ color: P.ink }} data-testid="obituary-name">
            {name}
          </h1>
          {nameMl && (
            <div className="mt-1 text-lg text-center" lang="ml" style={{ color: P.inkSoft, fontFamily: "'Noto Sans Malayalam', 'Manjari', system-ui, sans-serif" }} data-testid="obituary-name-ml">
              {nameMl}
            </div>
          )}
          <div className="mt-3 flex items-center gap-3 text-sm sm:text-base font-mono" style={{ color: P.inkSoft }}>
            <span data-testid="obituary-lifespan">{fmtDate(born) || "—"}</span>
            <span aria-hidden style={{ color: P.gold }}>—</span>
            <span data-testid="obituary-death">{fmtDate(died) || "—"}</span>
            {age && <span className="ml-2 italic">· {age} years</span>}
          </div>
          {loc && (
            <div className="mt-2 flex items-center gap-1.5 text-sm" style={{ color: P.inkSoft }} data-testid="obituary-location">
              <MapPin className="w-4 h-4" style={{ color: P.saffron }} /> {loc}
            </div>
          )}
        </div>

        <hr className="my-8 border-0 h-px" style={{ background: P.rule }} />

        {brief && (
          <div className="prose prose-lg max-w-none text-center" data-testid="obituary-remembrance">
            <p className="font-display italic text-lg sm:text-xl leading-relaxed" style={{ color: P.ink }}>
              {brief}
            </p>
          </div>
        )}

        {service && (
          <div className="mt-8 rounded-2xl border-2 p-5 sm:p-6" style={{ borderColor: `${P.gold}55`, background: "#FFFDF6" }} data-testid="obituary-service">
            <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest" style={{ color: P.saffron }}>
              <Calendar className="w-3.5 h-3.5" /> Service details
            </div>
            <div className="mt-2 text-sm sm:text-base whitespace-pre-line" style={{ color: P.ink }}>
              {typeof service === "string" ? service : (service.description || JSON.stringify(service))}
            </div>
            {service && service.venue && (
              <div className="mt-2 text-xs sm:text-sm flex items-center gap-1" style={{ color: P.inkSoft }}>
                <MapPin className="w-3.5 h-3.5" /> {service.venue}
              </div>
            )}
          </div>
        )}

        {family && family.length > 0 && (
          <div className="mt-8" data-testid="obituary-family">
            <div className="text-[10px] font-mono uppercase tracking-widest text-center" style={{ color: P.gold }}>
              <Users className="w-3.5 h-3.5 inline mr-1" /> Family
            </div>
            <div className="mt-3 flex flex-wrap gap-2 justify-center">
              {family.slice(0, 20).map((f, i) => (
                <span key={i} className="px-3 py-1 rounded-full text-xs border" style={{ borderColor: `${P.gold}55`, color: P.ink, background: "#FFFDF6" }}>
                  {typeof f === "string" ? f : (f.name || "")}{typeof f === "object" && f.relation ? <span className="opacity-60 italic ml-1">({f.relation})</span> : null}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Bridge to the permanent memorial — the whole point of the
            distinct URL is to give the dated notice a clean home while
            still funnelling grief-and-remembrance traffic to the
            evergreen memorial for the life story. */}
        <div className="mt-10 rounded-2xl p-5 sm:p-6 text-center" style={{ background: `${P.tulsi}0F`, border: `1px solid ${P.tulsi}55` }}>
          <div className="text-[10px] font-mono uppercase tracking-widest" style={{ color: P.tulsi }}>Permanent memorial</div>
          <div className="mt-1 font-display text-xl sm:text-2xl" style={{ color: P.ink }}>
            The life story of {name.split(" ")[0]} lives on.
          </div>
          <Link
            to={permanentUrl}
            data-testid="obituary-permanent-memorial-link"
            className="mt-4 inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-white text-sm font-semibold shadow hover:opacity-90"
            style={{ background: P.tulsi }}
          >
            View the memorial page <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        {m.source_url && (
          <div className="mt-6 text-center text-xs" style={{ color: P.inkSoft }}>
            <a href={m.source_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 underline hover:opacity-80">
              Original source <ExternalLink className="w-3 h-3" />
            </a>
          </div>
        )}

        <div className="mt-10 text-center text-xs" style={{ color: P.inkSoft }}>
          <Link to="/obituaries" className="underline hover:opacity-80" data-testid="obituary-directory-link">
            Browse recent obituaries
          </Link>
          <span className="mx-2">·</span>
          <Link to="/celebrate/submit" className="underline hover:opacity-80" data-testid="obituary-submit-link">
            Submit an obituary
          </Link>
        </div>
      </div>
    </div>
  );
}
